<template>
    <console-content max-width="2056">
        <v-row class="mt-4 px-1" align="center">
            <v-col cols="auto" class="tit tit--lg text-start">주문 관리</v-col>
            <v-spacer/>
            <v-col cols="auto">
                <v-btn class="mr-1" color="secondary" rounded @click="showSearch = !showSearch">
                    <v-icon small class="mr-2">mdi-filter-variant</v-icon>검색필터
                </v-btn>
            </v-col>
        </v-row>

        <v-expand-transition>
            <v-card v-show="showSearch" class="elevation-1 mt-2 mx-1">
                <v-card-title class="tit tit--xs font-weight-bold">검색필터</v-card-title>
                <v-card-text class="pb-0">
                    <v-row align="center">
                        <v-col cols="12" sm="6">
                            <v-date-field v-model="filter.createdAt[0]" label="주문일자" persistent-placeholder hide-details />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-date-field v-model="filter.createdAt[1]" label="주문일자" persistent-placeholder hide-details />
                        </v-col>
                    </v-row>
                    <v-row class="mt-2">
                        <v-col cols="12" sm="3">
                            <v-select v-model="filter.searchKey" :items="searchKeys" item-text="text" item-value="value" label="검색조건" persistent-placeholder hide-details @input="filter.searchValue = null;" />
                        </v-col>
                        <v-col cols="12" sm="3">
                            <v-text-field v-model="filter.searchValue" label="검색어" persistent-placeholder hide-details clearable :disabled="!filter.searchKey" @keydown.enter="page = 1; search(true)" />
                        </v-col>
                        <v-col cols="12" sm="3">
                            <v-select v-model="filter.status" :items="statusKeys" item-text="text" item-value="value" label="주문상태" persistent-placeholder hide-details />
                        </v-col>
                        <v-col cols="12" sm="3">
                            <v-select v-model="filter.cashbillStatus" :items="cashbillState" item-text="text" item-value="value" label="현금영수증 여부" persistent-placeholder hide-details />
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" rounded text @click="page = 1; search(true)">
                        <v-icon small class="mt-1 mr-2">mdi-magnify</v-icon>검색
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-expand-transition>

        <v-layout class="mt-4 px-1" align-end>
            <v-flex cols="auto" class="tit tit--xs">검색수 : {{ summary.totalCount.format() }}</v-flex>
            <v-flex v-if="results" cols="auto" class="tit tit--xs">①총 매출액 : {{ `${results?.totalAmount[0]?.totalAmount?.format() || 0} 원` }}</v-flex>
            <v-flex v-if="results" cols="auto" class="tit tit--xs">②취소금액 : {{ `${results?.returnedAmount[0]?.totalAmount?.format() || 0} 원` }}</v-flex>
            <v-flex v-if="results" cols="auto" class="tit tit--xs">③수수료 : {{ `${results?.commissionTotalAmount[0]?.totalAmount?.format() || 0} 원` }}</v-flex>
            <v-flex v-if="results" cols="auto" class="tit tit--xs">④정산금액(①-②-③) : {{ `${((results?.totalAmount[0]?.totalAmount || 0) - (results?.returnedAmount[0]?.totalAmount || 0) - (results?.commissionTotalAmount[0]?.totalAmount || 0) || 0)?.format()} 원` }}</v-flex>
            <v-spacer />
            <v-flex cols="auto" class="flex-grow-0">
                <v-btn color="white" class="green--text" elevation="1" @click="excel">
                    <v-icon left size="20">mdi-microsoft-excel</v-icon>엑셀다운로드
                </v-btn>
            </v-flex>
        </v-layout>

        <v-data-table :items="purchases" :headers="purchasesHeaders" hide-default-footer disable-pagination disable-sort class="mt-2 mx-1 elevation-1">
            <div slot="item.order.orderNo" slot-scope="{item}">
                <div>주문번호</div>
                <div>{{ item.order.orderNo }}</div>
                <div>구매번호</div>
                <div>{{ item.purchaseNo }}</div>
            </div>
            <span slot="item.quantity" slot-scope="{item}">{{ item.quantity.format() }}개</span>
            <template #[`item.product`]="{item}">
                <v-layout align-center>
                    <v-img :src="`https://daehanbooks.co.kr` + item?.product?.thumb" max-width="96" max-height="96" contain/>
                    <div class="pl-4">
                        <div><b>{{ item?.product?.name }}</b></div>
                        <div>{{ item?.product?.salePrice.format() }}원</div>
                        <div v-if="item?.product?.type == 'option'">[{{ item?.product?.optionalNames.toString() }}] : {{ item?.product?.optionalValues.toString() }}</div>
                        <div v-if="item?.product?.type == 'addition'">[{{ item?.product?.additionalName.toString() }}] : {{ item?.product?.additionalValue.toString() }}</div>
                    </div>
                </v-layout>
            </template>
            <!-- <template slot="item.option-addition" slot-scope="{item}">
                <div v-if="item.product.type == 'option'">[{{ item.product.optionalNames.toString() }}] : {{ item.product.optionalValues.toString() }}</div>
                <div v-if="item.product.type == 'addition'">[{{ item.product.additionalName.toString() }}] : {{ item.product.additionalValue.toString() }}</div>
            </template> -->
            <div slot="item.order.buyer" slot-scope="{item}">
                <div>{{ item.order.buyer.name }} ({{ item.order.buyer.username }})</div>
                <div>{{ item.order.buyer.phone }}</div>
                <div>{{ item.order.buyer.email }}</div>
            </div>
            <div slot="item.order.receiver" slot-scope="{item}">
                <div>{{ item.order.receiver.name }}</div>
                <div>{{ item.order.receiver.phone }}</div>
                <div>{{ item.order.receiver.email }}</div>
            </div>
            <div slot="item.order.shippingAddress" slot-scope="{item}">
                <div>[{{ item.order.shippingAddress.postcode }}]</div>
                <div>{{ item.order.shippingAddress.address1 }}</div>
                <div>{{ item.order.shippingAddress.address2 }}</div>
            </div>
            <div slot="item.shippingInfo" slot-scope="{item}">
                <div>{{ item.shippingInfo.method }}</div>
                <div>{{ item.shippingInfo.invoiceNumber }}</div>
                <div>{{ couriers.find(courier => courier.code == item.shippingInfo.courier)?.text }}</div>
            </div>
            <div slot="item.order.cashbill.status" slot-scope="{item}">
                <div v-if="item.order.cashbill.status">
                    <div>{{ item.order.cashbill.identityNum }}</div>
                    <div>신청</div>
                </div>
                <div v-else>미신청</div>
            </div>
            <span slot="item.product.costPrice" slot-scope="{item}">{{ item.product.costPrice.format() }}원</span>
            <span slot="item.order.totalAmount" slot-scope="{item}">{{ item.order.totalAmount.format() }}원</span>
            <span slot="item.ebookCommissionAmount" slot-scope="{item}">{{ item.ebookCommissionAmount.format() }}원</span>
            <span slot="item.order" slot-scope="{item}">{{ paymentWay(item)}}</span>
            <span slot="item.createdAt" slot-scope="{item}">{{ $dayjs(item.createdAt).format("YYYY-MM-DD HH:mm:ss") }}</span>
        </v-data-table>

        <v-pagination v-model="page" :length="pageCount" total-visible="11" class="mt-3" @input="search(true)"/>

    </console-content>
</template>

<script>
import api from "@/api";
import XLSX from "xlsx";
import ConsoleContent from "@/templates/console/ConsoleContent.vue";
import VDateField from "@/components/plugins/vuetify/v-date-field.vue";
import { mdiMicrosoftExcel } from "@mdi/js";
import couriers from "@/assets/data/couriers.json"

export default {
    components: {
        ConsoleContent,
        VDateField
    },
    data(){
        return {
            mdiMicrosoftExcel,

            showSearch: true,

            filter: {
                searchKey: this.$route.query.searchKey || null,
                searchValue: this.$route.query.searchValue || null,
                status: this.$route.query.status || null,
                createdAt: [ this.$route.query.createdAt?.[0] || '', this.$route.query.createdAt?.[1] || '' ],
                cashbillStatus: this.$route.query.cashbillStatus || null,
            },

            page: +this.$route.query.page || 1,
            pageCount: 0,
            limit: 10,
            cashbillStatus: null,

            summary: { totalCount: 0 },
            purchases: [],
            purchasesHeaders: [
                { text: "주문일자", value: "createdAt", align: "center", width: 110 },
                { text: "주문상태", value: "status", align: "center", width: 90 },
                { text: "결제상태", value: "paymentStatus", align: "center", width: 90 },
                { text: "주문번호/ 구매번호", value: "order.orderNo", align: "center", width: 10 },
                // { text: "구매번호", value: "purchaseNo", align: "center", width: 10 },
                { text: "구매상품", value: "product", align: "center", width: 200 },
                // { text: "구매옵션", value: "option-addition", align: "center", width: 200 },
                { text: "구매수량", value: "quantity", align: "center", width: 100 },
                { text: "결제방법", value: "order", align: "center", width: 100 },
                { text: "소비자가", value: "product.costPrice", align: "center", width: 90 },
                { text: "결제금액", value: "order.totalAmount", align: "center", width: 90 },
                { text: "ebook 수수료 금액", value: "ebookCommissionAmount", align: "center", width: 90 },
                // { text: "현금영수증", value: "order.cashbill.status", align: "center", width: 110 },
                // { text: "구매자", value: "order.buyer", align: "center", width: 100 },
                // { text: "수취인", value: "order.receiver", align: "center", width: 100 },
                // { text: "배송지", value: "order.shippingAddress", align: "center", width: 200 },
                // { text: "송장정보", value: "shippingInfo", align: "center", width: 120 },
            ],

            searchKeys: [
                { text: ":: 전체 ::", value: null },
                { text: "주문번호", value: "order.orderNo" },
                { text: "구매번호", value: "purchaseNo" },
                { text: "구매자 이름", value: "order.buyer.name" },
                { text: "구매자 아이디", value: "order.buyer.username" },
                { text: "구매자 연락처", value: "order.buyer.phone" },
                { text: "구매자 이메일", value: "order.buyer.email" },
                { text: "수취인 이름", value: "order.receiver.name" },
                { text: "수취인 연락처", value: "order.receiver.phone" },
                { text: "수취인 이메일", value: "order.receiver.email" },
            ],
            statusKeys: [
                { text: ":: 전체 ::", value: null },
                { text: "주문완료", value: "주문완료" },
                { text: "상품준비중", value: "상품준비중" },
                { text: "발송지연", value: "발송지연" },
                { text: "집하취소", value: "집하취소" },
                { text: "배송중", value: "배송중" },
                { text: "배송완료", value: "배송완료" },
                { text: "취소요청", value: "취소요청" },
                { text: "취소반려", value: "취소반려" },
                { text: "취소완료", value: "취소완료" },
                { text: "반품요청", value: "반품요청" },
                { text: "반품반려", value: "반품반려" },
                { text: "반품수거중", value: "반품수거중" },
                { text: "반품수거완료", value: "반품수거완료" },
                { text: "반품완료", value: "반품완료" },
            ],
            cashbillState: [
                { text: ":: 전체 ::", value: null },
                { text: "신청", value: "신청" },
                { text: "미신청", value: "미신청" },
            ],

            couriers,
            results: null
        }
    },
    mounted(){
        this.init();
    },
    methods: {
        init(){
            this.search(false);
        },
        async search(routable = false){
            if(routable){
                this.$router.push({
                    query: { ...this.filter, page: this.page }
                });
            }

            let { summary, purchases, results } = await api.console.shop.purchases.gets({
                headers: {
                    skip: (this.page - 1) * this.limit,
                    limit: this.limit
                },
                params: this.filter
            });

            this.purchases = purchases;
            this.summary = summary;
            this.results = results?.[0] || null;
            this.pageCount = Math.ceil(summary.totalCount / this.limit);
        },
        async excel(){
            let { purchases } = await api.console.shop.purchases.gets({ headers: { limit: 0 }, params: this.filter });

            let rows = [];
            purchases.forEach(purchase => {
                rows.push({
                    "주문일자": this.$dayjs(purchase?.createdAt).format("YYYY-MM-DD HH:mm:ss"),
                    "주문상태": purchase?.status,
                    "결제상태": purchase?.paymentStatus,
                    "주문번호": purchase?.order?.orderNo,
                    "구매번호": purchase?.purchaseNo,
                    "구매상품": purchase?.product?.name,
                    "구매옵션": (purchase?.product?.type == "option" || purchase?.product?.type == "addition") ? `[${purchase.product.optionalNames?.toString?.() || purchase.product.additionalName }] : ${purchase.product.optionalValues?.toString?.() || purchase.product.additionalValue }` : "",
                    "구매수량": purchase?.quantity?.format?.(),
                    "결제방법": this.paymentWay(purchase),
                    "소비자가": purchase?.product.costPrice?.format?.(),
                    "결제금액": purchase?.order?.totalAmount?.format?.(),
                    "포인트 사용금액": purchase?.order?.pointAmount?.format?.(),
                    "캐시 사용금액": purchase?.order?.cashAmount?.format?.(),
                    // "구매자 이름": purchase?.order?.buyer?.name,
                    // "구매자 아이디": purchase?.order?.buyer?.username,
                    // "구매자 연락처": purchase?.order?.buyer?.phone,
                    // "구매자 이메일": purchase?.order?.buyer?.email,
                    // "수취인 이름": purchase?.order?.receiver?.name,
                    // "수취인 연락처": purchase?.order?.receiver?.phone,
                    // "수취인 이메일": purchase?.order?.receiver?.email,
                    // "배송지": purchase?.order?.shippingAddress?.postcode ? `[${purchase?.order?.shippingAddress?.postcode}] ${purchase?.order?.shippingAddress?.address1} ${purchase?.order?.shippingAddress?.address2}` : '-',
                    // "배송방법": purchase?.shippingInfo?.method || '-',
                    // "운송장번호": purchase?.shippingInfo?.invoiceNumber || '-',
                    // "택배사": couriers.find(courier => courier.code == purchase?.shippingInfo?.courier)?.text || '-',
                    // "선물상태": purchase?.giftStatus,
                });
            });

            let workbook = new XLSX.utils.book_new();
            let worksheet = XLSX.utils.json_to_sheet(rows);

            XLSX.utils.book_append_sheet(workbook, worksheet, "주문내역");
            XLSX.writeFile(workbook, "주문내역.xlsx");
        },
        paymentWay(item){
            let str = ""
            if(item?.order?.type === 'exchange') return '모바일 도서교환권 사용'
            if(item?.order?.cashAmount && item?.order?.pointAmount && !item?.order?.paymentAmount) str =  '캐시 & 포인트 결제'
            if(item?.order?.cashAmount && !item?.order?.pointAmount && !item?.order?.paymentAmount) str =  '캐시 결제'
            if(!item?.order?.cashAmount && item?.order?.pointAmount && !item?.order?.paymentAmount) str =  '포인트 결제'
            if(!item?.order?.cashAmount && !item?.order?.pointAmount && item?.order?.paymentAmount) str =  '신용카드 결제'
            if(!item?.order?.cashAmount && item?.order?.pointAmount && item?.order?.paymentAmount) str =  '신용카드 & 포인트 결제'
            if(item?.order?.cashAmount && !item?.order?.pointAmount && item?.order?.paymentAmount) str =  '신용카드 & 캐시 결제'
            if(item?.order?.cashAmount && item?.order?.pointAmount && item?.order?.paymentAmount) str =  '신용카드 & 캐시 & 포인트 결제'
            return str
        },
    }
}
</script>